import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Select,
  Box,
  FormControl,
  TextField,
  Tooltip,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import InfoIcon from '@mui/icons-material/Info'
import AddIcon from '@mui/icons-material/Add'
import { Clinic, Role, RoleForClinics } from '../../../types'
import ClinicPicker from '../../../components/ClinicPicker'

interface Props {
  open: boolean
  rolesForClinics: RoleForClinics[]
  roleOptions: Role[]
  clinicOptions: Clinic[]
  onClose: () => void
  onSave: (rolesForClinics: RoleForClinics[]) => void
}

const RolesForClinicsDialog: React.FC<Props> = ({
  open,
  rolesForClinics,
  roleOptions,
  clinicOptions,
  onClose,
  onSave,
}) => {
  const clinicOptionsSorted = useMemo(
    () => clinicOptions.sort((clinicA, clinicB) => clinicA.name.localeCompare(clinicB.name)),
    [clinicOptions]
  )
  const [localRolesForClinics, setLocalRolesForClinics] = useState<RoleForClinics[]>(rolesForClinics)

  useEffect(() => {
    if (open) {
      setLocalRolesForClinics(rolesForClinics)
    }
  }, [open, rolesForClinics])

  const handleAddRole = () => {
    if (roleOptions.length > 0) {
      setLocalRolesForClinics([
        ...localRolesForClinics,
        {
          role: roleOptions[0],
          clinics: [],
          validUntil: null,
        },
      ])
    }
  }

  const handleRemoveRole = (index: number) => {
    setLocalRolesForClinics(localRolesForClinics.filter((_, i) => i !== index))
  }

  const handleRoleChange = (index: number, roleId: string) => {
    const role = roleOptions.find((r) => r.referenceId === roleId)
    if (role) {
      const updated = [...localRolesForClinics]
      updated[index] = { ...updated[index], role }
      setLocalRolesForClinics(updated)
    }
  }

  const onChangeClinics = (forRole: Role, newClinics: Clinic[]) => {
    const rolesForClinicsChanged = [...localRolesForClinics]
    const idx = rolesForClinicsChanged.findIndex(({ role }) => role === forRole)
    if (idx > -1) {
      rolesForClinicsChanged[idx] = { ...rolesForClinicsChanged[idx], clinics: newClinics }
    }
    setLocalRolesForClinics(rolesForClinicsChanged)
  }

  const handleClinicsChange = (role: Role) => (newClinics: Clinic[]) => onChangeClinics(role, newClinics)

  const handleValidUntilChange = (index: number, value: string) => {
    const updated = [...localRolesForClinics]
    updated[index] = {
      ...updated[index],
      validUntil: value || null,
    }
    setLocalRolesForClinics(updated)
  }

  const handleSave = () => {
    onSave(localRolesForClinics)
    onClose()
  }

  const handleCancel = () => {
    setLocalRolesForClinics(rolesForClinics)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Edit Role Assignments</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Clinics</TableCell>
                <TableCell>
                  Valid Until{' '}
                  <Tooltip title={`If set, the role will be automatically removed after this date`} arrow>
                    <InfoIcon sx={{ fontSize: '16px', verticalAlign: 'text-top' }} />
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {localRolesForClinics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No roles assigned. Click "Add Role" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                localRolesForClinics.map((roleForClinics, index) => (
                  <TableRow key={`${roleForClinics.role.referenceId}-${index}`}>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={roleForClinics.role.referenceId}
                          onChange={(e) => handleRoleChange(index, e.target.value)}
                        >
                          {roleOptions.map((role) => (
                            <MenuItem key={role.referenceId} value={role.referenceId}>
                              {role.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <ClinicPicker
                        value={roleForClinics.clinics}
                        options={clinicOptionsSorted}
                        onChange={handleClinicsChange(roleForClinics.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="Valid Until"
                        type="date"
                        value={roleForClinics.validUntil || ''}
                        onChange={(e) => handleValidUntilChange(index, e.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ lang: 'de-DE' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleRemoveRole(index)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRole}
            sx={{ mt: 2 }}
            disabled={roleOptions.length === 0}
          >
            Add Role
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RolesForClinicsDialog
